import { Document, Page, StyleSheet, View, Text } from "@react-pdf/renderer";

// Create styles
const styles = StyleSheet.create({
  page: {
    backgroundColor: "#fff",
    padding: 10,
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
    paddingBottom: 2,
    borderBottomWidth: 2,
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  tableContainer: {
    width: "100%",
    borderStyle: "solid",
    marginTop: 10,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableColHeader: {
    fontSize: 10,
    fontWeight: "extrabold",
    textAlign: "center",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderBottomWidth: 1,
    padding: 3,
  },
  tableCol: {
    fontSize: 9,
    textAlign: "center",
    borderStyle: "solid",
    justifyContent: "center",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 3,
  },
  tableFirstCol: {
    borderLeftWidth: 1,
  },
  holidayRow: {
    backgroundColor: "#e6ffe6", // Light green for holiday
  },
  absentRow: {
    backgroundColor: "#ffebeb", // Light red for absent
  },
  departmentHeader: {
    backgroundColor: "#f0f0f0",
    padding: 5,
    fontSize: 11,
    fontWeight: "bold",
    marginTop: 5,
    marginBottom: 2,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 8,
    color: "grey",
  },
});

// Create Document Component
export const AttendanceByDateReport = ({ selectedReport }) => {
  /*   All Functions   ********************************************* */
  // Function to format date as MM/DD/YYYY
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  };

  // Create a function to convert "18:11:56" to "6:11 PM"
  function formatTime(timeString) {
    if (typeof timeString !== "string") {
      return "-";
    }

    // Split the time string into hours, minutes, and seconds
    let [hours, minutes] = timeString?.split(":").map(Number);

    const period = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12; // Handle midnight (0) and 12 PM
    const formattedMinutes = minutes.toString().padStart(2, "0");
    return `${formattedHours}:${formattedMinutes} ${period}`;
  }

  // Sort users by department first, then by name
  const getSortedUsers = () => {
    if (!selectedReport || !Array.isArray(selectedReport)) return [];

    return [...selectedReport].sort((a, b) => {
      // check if department exists
      if (!a.department || !b.department) {
        console.error("Missing department in user data:", a, b);
        return 0; // If department is missing, do not change order
      }

      // First sort by department
      const deptCompare = a.department.localeCompare(b.department);

      // If departments are the same, sort by name
      if (deptCompare === 0) {
        return a.userData.name.localeCompare(b.userData.name);
      }

      return deptCompare;
    });
  };

  // Group users by department
  const getUsersByDepartment = () => {
    const sortedUsers = getSortedUsers();
    const departments = {};

    sortedUsers.forEach((user) => {
      if (!departments[user.department]) {
        departments[user.department] = [];
      }
      departments[user.department].push(user);
    });

    return departments;
  }; // Column widths for the table
  const colWidths = {
    name: "20%",
    department: "13%",
    status: "10%",
    inTime: "12%",
    outTime: "12%",
    lateTime: "11%",
    overtime: "11%",
    overtimeSummary: "33%",
    workingHours: "11%",
  };

  const departmentGroups = getUsersByDepartment();
  // Split users into chunks for pagination (10 users per page)
  const sortedUsers = getSortedUsers();
  const usersPerPage = 35;
  const totalPages = Math.ceil(sortedUsers.length / usersPerPage);

  // Create page data
  const createPages = () => {
    const allDepartmentGroups = { ...departmentGroups };
    const pages = [];

    let currentPage = 1;
    let remainingDepartments = { ...allDepartmentGroups };

    while (Object.keys(remainingDepartments).length > 0) {
      const pageData = {};
      let userCount = 0;

      // Add departments to this page until we reach the user limit
      for (const dept of Object.keys(remainingDepartments)) {
        if (!pageData[dept]) pageData[dept] = [];

        // Add users from this department until we reach page limit
        while (
          remainingDepartments[dept].length > 0 &&
          userCount < usersPerPage
        ) {
          pageData[dept].push(remainingDepartments[dept].shift());
          userCount++;
        }

        // If department is empty, remove it
        if (remainingDepartments[dept].length === 0) {
          delete remainingDepartments[dept];
        }

        // If we've reached page limit, stop adding departments
        if (userCount >= usersPerPage) {
          break;
        }
      }

      pages.push({ pageNum: currentPage, departments: pageData });
      currentPage++;
    }

    return pages;
  };

  const pages = createPages();
  // Table header component to reuse on each page
  const TableHeader = () => (
    <View style={styles.tableRow}>
      <View
        style={[
          styles.tableColHeader,
          styles.tableFirstCol,
          { width: colWidths.name },
        ]}
      >
        <Text>Employee Name</Text>
      </View>
      <View style={[styles.tableColHeader, { width: colWidths.department }]}>
        <Text>Department</Text>
      </View>
      <View style={[styles.tableColHeader, { width: colWidths.status }]}>
        <Text>Status</Text>
      </View>
      <View style={[styles.tableColHeader, { width: colWidths.inTime }]}>
        <Text>In Time</Text>
      </View>
      <View style={[styles.tableColHeader, { width: colWidths.outTime }]}>
        <Text>Out Time</Text>
      </View>
      <View style={[styles.tableColHeader, { width: colWidths.lateTime }]}>
        <Text>Late</Text>
      </View>
      <View style={[styles.tableColHeader, { width: colWidths.overtime }]}>
        <Text>Overtime</Text>
      </View>
      <View style={[styles.tableColHeader, { width: colWidths.workingHours }]}>
        <Text>Hours</Text>
      </View>
    </View>
  );

  // Get users with overtime
  const getUsersWithOvertime = (departmentFilter) => {
    if (!selectedReport || !Array.isArray(selectedReport)) return [];

    let filteredUsers = selectedReport.filter((user) => {
      // Check if user has overtime greater than 0
      return (
        user.attendance.overTime && parseFloat(user.attendance.overTime) > 0
      );
    });

    // Apply department filter if provided
    if (departmentFilter) {
      filteredUsers = filteredUsers.filter(
        (user) => user.department && user.department === departmentFilter
      );
    }

    // Sort alphabetically by department
    filteredUsers.sort((a, b) => {
      if (!a.department || !b.department) return 0;
      return a.department.localeCompare(b.department);
    });

    // Return the filtered and sorted users
    return filteredUsers;
  };

  const usersWithOvertime = getUsersWithOvertime();
  const hasOvertimeUsers = usersWithOvertime.length > 0;

  // Count present and absent users
  const getUserCounts = () => {
    if (!selectedReport || !Array.isArray(selectedReport))
      return { present: 0, absent: 0 };

    const counts = {
      present: 0,
      absent: 0,
    };

    selectedReport.forEach((user) => {
      if (user.attendance.message === "Absent") {
        counts.absent++;
      } else if (user.attendance.message !== "Holiday") {
        counts.present++;
      }
    });

    return counts;
  };

  const userCounts = getUserCounts();

  // Page header component to reuse
  const PageHeader = ({ pageNum }) => (
    <>
      <View style={styles.title}>
        <Text>Daily Attendance Report</Text>
      </View>
      <View style={styles.subtitle}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
          <Text style={{ fontSize: 11, fontWeight: "bold" }}>
            Date: {formatDate(selectedReport[0]?.attendance?.formattedDate)}
          </Text>
          <Text style={{ fontSize: 11, fontWeight: "bold" }}>
            Day: {selectedReport[0]?.attendance?.dayNameOfWeek}
          </Text>
        </View>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: 0,
            gap: 20,
          }}
        >
          <Text style={{ fontSize: 11, fontWeight: "bold" }}>
            Present: {userCounts.present}
          </Text>
          <Text style={{ fontSize: 11, fontWeight: "bold" }}>
            Absent: {userCounts.absent}
          </Text>
          <Text style={{ fontSize: 11, fontWeight: "bold" }}>
            Total: {userCounts.present + userCounts.absent}
          </Text>
        </View>
        <Text style={{ fontSize: 11, fontWeight: "bold" }}>
          Page {pageNum} of {totalPages}
        </Text>
      </View>
    </>
  );

  // Create pages for the overtime report
  const createOvertimePages = () => {
    if (!hasOvertimeUsers) return [];

    const overtimeUsersPerPage = 36; // Same as regular attendance pages
    const overtimePages = [];
    let currentPage = 1;

    // Group overtime users by department
    const overtimeDeptGroups = {};
    usersWithOvertime.forEach((user) => {
      if (!overtimeDeptGroups[user.department]) {
        overtimeDeptGroups[user.department] = [];
      }
      overtimeDeptGroups[user.department].push(user);
    });

    let remainingDepts = { ...overtimeDeptGroups };

    while (Object.keys(remainingDepts).length > 0) {
      const pageData = {};
      let userCount = 0;
      let spacerCount = 0; // Count department separator rows

      // Add departments to this page until we reach the user limit
      for (const dept of Object.keys(remainingDepts)) {
        if (!pageData[dept]) {
          pageData[dept] = [];
          // Count department separator if it's not the first department on the page
          if (Object.keys(pageData).length > 1) {
            spacerCount++;
          }
        }

        // Add users from this department until we reach page limit
        // Accounting for spacer rows in the count
        while (
          remainingDepts[dept].length > 0 &&
          userCount + spacerCount < overtimeUsersPerPage
        ) {
          pageData[dept].push(remainingDepts[dept].shift());
          userCount++;
        }

        // If department is empty, remove it
        if (remainingDepts[dept].length === 0) {
          delete remainingDepts[dept];
        }

        // If we've reached page limit, stop adding departments
        if (userCount + spacerCount >= overtimeUsersPerPage) {
          break;
        }
      }

      overtimePages.push({
        pageNum: currentPage,
        departments: pageData,
        spacerCount: spacerCount,
      });
      currentPage++;
    }

    return overtimePages;
  };

  const overtimePages = createOvertimePages();
  const totalOvertimePages = overtimePages.length;

  // Calculate total overtime hours and late time
  const calculateTotals = () => {
    let totalOvertime = 0;
    let totalLateTime = 0;

    usersWithOvertime.forEach((user) => {
      if (user.attendance.overTime) {
        totalOvertime += parseFloat(user.attendance.overTime);
      }
      if (user.attendance.totalLateTime) {
        totalLateTime += parseFloat(user.attendance.totalLateTime);
      }
    });

    return {
      overtime: totalOvertime.toFixed(2),
      lateTime: totalLateTime.toFixed(2),
    };
  };

  const totals = calculateTotals();
  const totalOvertimeHours = totals.overtime;
  const totalLateTime = totals.lateTime;

  // Overtime Page Header
  const OvertimePageHeader = ({ pageNum }) => (
    <>
      <View style={styles.title}>
        <Text>Overtime Report</Text>
      </View>
      <View style={styles.subtitle}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
          <Text style={{ fontSize: 11, fontWeight: "bold" }}>
            Date: {formatDate(selectedReport[0]?.attendance?.formattedDate)}
          </Text>
          <Text style={{ fontSize: 11, fontWeight: "bold" }}>
            Day: {selectedReport[0]?.attendance?.dayNameOfWeek}
          </Text>
        </View>
        <View style={{ marginBottom: 0 }}>
          <Text
            style={{
              fontSize: 12,
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            Employees with Overtime ({usersWithOvertime.length})
          </Text>
        </View>
        <Text style={{ fontSize: 11, fontWeight: "bold" }}>
          Page {pageNum} of {totalOvertimePages}
        </Text>
      </View>
    </>
  );

  // Overtime Table Header component - add Late column
  const OvertimeTableHeader = () => (
    <View style={styles.tableRow}>
      <View
        style={[
          styles.tableColHeader,
          styles.tableFirstCol,
          { width: colWidths.name },
        ]}
      >
        <Text>Employee Name</Text>
      </View>
      <View style={[styles.tableColHeader, { width: colWidths.department }]}>
        <Text>Department</Text>
      </View>
      <View style={[styles.tableColHeader, { width: colWidths.inTime }]}>
        <Text>In Time</Text>
      </View>
      <View style={[styles.tableColHeader, { width: colWidths.outTime }]}>
        <Text>Out Time</Text>
      </View>
      <View style={[styles.tableColHeader, { width: colWidths.lateTime }]}>
        <Text>Late</Text>
      </View>
      <View style={[styles.tableColHeader, { width: "22%" }]}>
        <Text>Overtime</Text>
      </View>
      <View style={[styles.tableColHeader, { width: colWidths.workingHours }]}>
        <Text>Hours</Text>
      </View>
    </View>
  );

  return (
    <Document title={"Daily Attendance Report"}>
      {/* Regular attendance pages */}
      {pages.map((page) => (
        <Page key={page.pageNum} size="LETTER" style={styles.page}>
          <PageHeader pageNum={page.pageNum} />

          {/* Attendance Table */}
          <View style={styles.tableContainer}>
            {/* Table Header */}
            <TableHeader />

            {/* Department Groups */}
            {Object.entries(page.departments).map(([department, users]) => (
              <View key={department}>
                {/* Users in Department */}
                {users.map((userData, index) => (
                  <View
                    key={index}
                    style={[
                      styles.tableRow,
                      userData.attendance.message === "Absent"
                        ? styles.absentRow
                        : userData.attendance.message === "Holiday"
                        ? styles.holidayRow
                        : null,
                    ]}
                  >
                    {" "}
                    <View
                      style={[
                        styles.tableCol,
                        styles.tableFirstCol,
                        { width: colWidths.name, textAlign: "left" },
                      ]}
                    >
                      <Text>{userData.userData.name}</Text>
                    </View>
                    <View
                      style={[styles.tableCol, { width: colWidths.department }]}
                    >
                      <Text>{userData.department}</Text>
                    </View>
                    <View
                      style={[styles.tableCol, { width: colWidths.status }]}
                    >
                      <Text>{userData.attendance.message || "Present"}</Text>
                    </View>
                    <View
                      style={[styles.tableCol, { width: colWidths.inTime }]}
                    >
                      <Text>
                        {formatTime(userData.attendance.inTime) || "-"}
                      </Text>
                    </View>
                    <View
                      style={[styles.tableCol, { width: colWidths.outTime }]}
                    >
                      <Text>
                        {formatTime(userData.attendance.outTime) || "-"}
                      </Text>
                    </View>
                    <View
                      style={[styles.tableCol, { width: colWidths.lateTime }]}
                    >
                      <Text>
                        {userData.attendance.totalLateTime?.toFixed(2) ||
                          "0.00"}
                      </Text>
                    </View>
                    <View
                      style={[styles.tableCol, { width: colWidths.overtime }]}
                    >
                      <Text>
                        {userData.attendance.overTime?.toFixed(2) || "0.00"}
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.tableCol,
                        { width: colWidths.workingHours },
                      ]}
                    >
                      <Text>
                        {userData.attendance.totalHours?.toFixed(2) || "0.00"}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            ))}
          </View>

          <View style={styles.footer}>
            <Text>Report generated on {new Date().toLocaleString()}</Text>
          </View>
        </Page>
      ))}

      {/* Overtime Users Pages - now paginated */}
      {hasOvertimeUsers &&
        overtimePages.map((page) => (
          <Page
            key={`overtime-${page.pageNum}`}
            size="LETTER"
            style={styles.page}
          >
            <OvertimePageHeader pageNum={page.pageNum} />

            {/* Overtime Table */}
            <View style={styles.tableContainer}>
              <OvertimeTableHeader />

              {/* Department Groups */}
              {Object.entries(page.departments).map(
                ([department, users], deptIndex) => (
                  <View key={department}>
                    {/* Add department separator if not the first department */}
                    {deptIndex > 0 && (
                      <View
                        style={{
                          ...styles.tableRow,
                          backgroundColor: "lightgrey",
                        }}
                      >
                        <View
                          style={[
                            styles.tableCol,
                            styles.tableFirstCol,
                            { width: colWidths.name },
                          ]}
                        ></View>
                        <View
                          style={[
                            styles.tableCol,
                            { width: colWidths.department },
                          ]}
                        ></View>
                        <View
                          style={[styles.tableCol, { width: colWidths.inTime }]}
                        ></View>
                        <View
                          style={[
                            styles.tableCol,
                            { width: colWidths.outTime },
                          ]}
                        ></View>
                        <View
                          style={[
                            styles.tableCol,
                            { width: colWidths.lateTime },
                          ]}
                        ></View>
                        <View
                          style={[styles.tableCol, { width: "22%" }]}
                        ></View>
                        <View
                          style={[
                            styles.tableCol,
                            { width: colWidths.workingHours },
                          ]}
                        ></View>
                      </View>
                    )}

                    {/* Users in Department in overtime section */}
                    {users.map((userData, index) => (
                      <View key={index} style={styles.tableRow}>
                        <View
                          style={[
                            styles.tableCol,
                            styles.tableFirstCol,
                            { width: colWidths.name, textAlign: "left" },
                          ]}
                        >
                          <Text>{userData.userData.name}</Text>
                        </View>
                        <View
                          style={[
                            styles.tableCol,
                            { width: colWidths.department },
                          ]}
                        >
                          <Text>{userData.department}</Text>
                        </View>
                        <View
                          style={[styles.tableCol, { width: colWidths.inTime }]}
                        >
                          <Text>
                            {formatTime(userData.attendance.inTime) || "-"}
                          </Text>
                        </View>
                        <View
                          style={[
                            styles.tableCol,
                            { width: colWidths.outTime },
                          ]}
                        >
                          <Text>
                            {formatTime(userData.attendance.outTime) || "-"}
                          </Text>
                        </View>
                        <View
                          style={[
                            styles.tableCol,
                            { width: colWidths.lateTime },
                          ]}
                        >
                          <Text>
                            {userData.attendance.totalLateTime?.toFixed(2) ||
                              "0.00"}
                          </Text>
                        </View>
                        <View style={[styles.tableCol, { width: "22%" }]}>
                          <Text style={{ fontWeight: "bold" }}>
                            {userData.attendance.overTime?.toFixed(2) || "0.00"}
                          </Text>
                        </View>
                        <View
                          style={[
                            styles.tableCol,
                            { width: colWidths.workingHours },
                          ]}
                        >
                          <Text>
                            {userData.attendance.totalHours?.toFixed(2) ||
                              "0.00"}
                          </Text>
                        </View>
                      </View>
                    ))}
                  </View>
                )
              )}

              {/* Add totals row on the last page */}
              {page.pageNum === totalOvertimePages && (
                <View
                  style={[
                    styles.tableRow,
                    { marginTop: 10, borderTopWidth: 1, fontWeight: "bold" },
                  ]}
                >
                  <View
                    style={[
                      styles.tableCol,
                      styles.tableFirstCol,
                      {
                        width: colWidths.name,
                        textAlign: "right",
                      },
                    ]}
                  >
                    <Text style={{ fontWeight: "bold" }}>Totals:</Text>
                  </View>
                  <View
                    style={[styles.tableCol, { width: colWidths.department }]}
                  >
                    <Text></Text>
                  </View>
                  <View style={[styles.tableCol, { width: colWidths.inTime }]}>
                    <Text></Text>
                  </View>
                  <View style={[styles.tableCol, { width: colWidths.outTime }]}>
                    <Text></Text>
                  </View>
                  <View
                    style={[styles.tableCol, { width: colWidths.lateTime }]}
                  >
                    <Text style={{ fontWeight: "bold" }}>{totalLateTime}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: "22%" }]}>
                    <Text style={{ fontWeight: "bold" }}>
                      {totalOvertimeHours}
                    </Text>
                  </View>
                  <View
                    style={[styles.tableCol, { width: colWidths.workingHours }]}
                  >
                    <Text></Text>
                  </View>
                </View>
              )}
            </View>

            <View style={styles.footer}>
              <Text>Report generated on {new Date().toLocaleString()}</Text>
            </View>
          </Page>
        ))}
    </Document>
  );
};
